/**
 * D91 / Piece C — Publish Results Dialog.
 *
 * Opens after the user clicks "Publish now" in the Post Composer.
 * Polls GET /api/social/posts/{id} every ~2.5s and renders a live
 * per-target status table: pending / publishing / published / failed
 * (and per-platform sub-rows for FB+IG).
 *
 * Offers "Retry failed" if any target ends in failed/pending.
 *
 * Defensive rules:
 *   - No empty-string Radix Select values (no selects here, but kept
 *     pattern consistent if/when filters are added).
 *   - Null-safe access on every field.
 *   - Polling stops as soon as the post leaves status='publishing' or
 *     after MAX_POLLS so this never spins forever.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { socialAPI } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Loader2, CheckCircle2, XCircle, Clock, AlertTriangle, RefreshCw,
  Facebook, Instagram, ExternalLink, ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const POLL_INTERVAL_MS = 2500;
const POLL_MAX_DURATION_MS = 10 * 60 * 1000; // 10 minutes hard cap.

const STATUS_META = {
  pending:    { Icon: Clock,        cls: 'text-muted-foreground border-muted-foreground/30', label: 'Pending' },
  publishing: { Icon: Loader2,      cls: 'text-blue-300 border-blue-400/40 animate-spin-host', label: 'Publishing' },
  published:  { Icon: CheckCircle2, cls: 'text-emerald-400 border-emerald-400/40', label: 'Published' },
  partial:    { Icon: AlertTriangle,cls: 'text-amber-300 border-amber-400/40', label: 'Partial' },
  failed:     { Icon: XCircle,      cls: 'text-destructive border-destructive/40', label: 'Failed' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.Icon;
  const spinning = status === 'publishing';
  return (
    <Badge variant="outline" className={`text-[10px] ${meta.cls}`} data-testid={`d91-status-${status}`}>
      <Icon className={`h-3 w-3 mr-1 ${spinning ? 'animate-spin' : ''}`} />
      {meta.label}
    </Badge>
  );
}

function TargetRow({ target, expanded, onToggle }) {
  const status = target?.status || 'pending';
  const sa = target?.social_account_id;
  const platforms = Array.isArray(target?.platforms) ? target.platforms : [];
  const results = target?.results || {};

  return (
    <div className="border-b border-border/20 last:border-b-0" data-testid={`d91-target-row-${sa}`}>
      <div className="flex items-center gap-2 p-2 hover:bg-foreground/[0.02]">
        <button type="button" onClick={onToggle} className="p-1 -ml-1 shrink-0"
                data-testid={`d91-target-expand-${sa}`} aria-label="Expand">
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-medium truncate">{target?.page_name || 'Untitled Page'}</div>
          <div className="text-[10px] text-muted-foreground flex items-center gap-2 truncate">
            {platforms.includes('facebook') && <span className="inline-flex items-center gap-0.5"><Facebook className="h-3 w-3" />FB</span>}
            {platforms.includes('instagram') && target?.ig_username && (
              <span className="inline-flex items-center gap-0.5 text-pink-400"><Instagram className="h-3 w-3" />@{target.ig_username}</span>
            )}
            {target?.attempts > 0 && <span>· {target.attempts} attempt{target.attempts === 1 ? '' : 's'}</span>}
          </div>
        </div>
        <StatusBadge status={status} />
      </div>
      {expanded && (
        <div className="px-8 pb-2 space-y-1.5 text-[11px]" data-testid={`d91-target-detail-${sa}`}>
          {platforms.map((p) => {
            const r = results?.[p] || {};
            const Icon = p === 'facebook' ? Facebook : Instagram;
            const ok = !!r.ok;
            const id = r.published_id;
            const link = r.permalink;
            return (
              <div key={p} className="flex items-start gap-2 py-1 border-t border-border/15 first:border-t-0">
                <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${p === 'facebook' ? 'text-[#4d94ff]' : 'text-pink-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium capitalize">{p}</div>
                  {ok ? (
                    <div className="text-emerald-400/90 text-[10px] flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Published{id ? ` · ${id}` : ''}
                      {link && (
                        <a href={link} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-0.5 text-gold underline ml-1">
                          View <ExternalLink className="h-2.5 w-2.5" />
                        </a>
                      )}
                    </div>
                  ) : r.error ? (
                    <div className="text-destructive/90 text-[10px]" data-testid={`d91-error-${sa}-${p}`}>
                      <span className="font-medium">Error:</span>{' '}{r.error.message || 'Failed'}
                      {r.error.fbtrace_id && (
                        <span className="text-muted-foreground/70 ml-1">(trace {r.error.fbtrace_id})</span>
                      )}
                    </div>
                  ) : status === 'pending' ? (
                    <div className="text-muted-foreground text-[10px]">Waiting…</div>
                  ) : (
                    <div className="text-muted-foreground text-[10px]">No result yet</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function PublishResultsDialog({ open, workspaceId, postId, onClose }) {
  const [post, setPost] = useState(null);
  const [polling, setPolling] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [expanded, setExpanded] = useState({});  // {social_account_id: true}
  const [filterFailed, setFilterFailed] = useState(false);
  const pollRef = useRef(null);
  const startedAt = useRef(0);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setPolling(false);
  };

  const fetchOnce = async () => {
    if (!workspaceId || !postId) return;
    try {
      const r = await socialAPI.getPost(workspaceId, postId);
      setPost(r?.data || null);
      const s = r?.data?.status;
      if (s !== 'publishing') {
        stopPolling();
      }
      if (Date.now() - startedAt.current > POLL_MAX_DURATION_MS) {
        stopPolling();
      }
    } catch (e) {
      // soft-fail; one polling tick won't be enough to stop the world.
    }
  };

  useEffect(() => {
    if (!open || !workspaceId || !postId) return;
    setExpanded({});
    setPost(null);
    setPolling(true);
    startedAt.current = Date.now();
    fetchOnce();
    pollRef.current = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return stopPolling;
  }, [open, workspaceId, postId]);

  const targets = useMemo(() => Array.isArray(post?.targets) ? post.targets : [], [post]);
  const counts = useMemo(() => {
    const c = { published: 0, failed: 0, partial: 0, publishing: 0, pending: 0 };
    targets.forEach((t) => { c[t?.status || 'pending'] = (c[t?.status || 'pending'] || 0) + 1; });
    return c;
  }, [targets]);

  const filteredTargets = useMemo(() => {
    if (!filterFailed) return targets;
    return targets.filter((t) => ['failed', 'pending', 'partial'].includes(t?.status));
  }, [targets, filterFailed]);

  const hasRetryable = (counts.failed + counts.pending) > 0;
  const overall = post?.status;

  const retry = async () => {
    setRetrying(true);
    try {
      await socialAPI.retryPost(workspaceId, postId);
      toast.success('Retry started for failed targets');
      // Re-arm the polling loop.
      setPolling(true);
      startedAt.current = Date.now();
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(fetchOnce, POLL_INTERVAL_MS);
      fetchOnce();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { stopPolling(); onClose && onClose(); } }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col" data-testid="d91-publish-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Publishing
            {overall && (
              <span data-testid="d91-overall-status">
                <StatusBadge status={overall} />
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {polling
              ? 'Sending to your selected accounts… Stay on this screen or check back later.'
              : 'Publish finished. Review per-target results below.'}
          </DialogDescription>
        </DialogHeader>

        {!post ? (
          <div className="flex items-center justify-center py-12" data-testid="d91-loading">
            <Loader2 className="h-5 w-5 animate-spin text-gold/50" />
            <span className="ml-2 text-[12px] text-muted-foreground">Loading post…</span>
          </div>
        ) : targets.length === 0 ? (
          <div className="py-12 text-[12px] text-muted-foreground text-center" data-testid="d91-empty">
            This post has no targets.
          </div>
        ) : (
          <>
            {/* Counters */}
            <div className="flex items-center flex-wrap gap-2 text-[11px]" data-testid="d91-counters">
              <Badge variant="outline" className="border-emerald-400/40 text-emerald-300">
                <CheckCircle2 className="h-3 w-3 mr-1" />Published <span className="ml-1 font-medium">{counts.published}</span>
              </Badge>
              {counts.partial > 0 && (
                <Badge variant="outline" className="border-amber-400/40 text-amber-300">
                  <AlertTriangle className="h-3 w-3 mr-1" />Partial <span className="ml-1 font-medium">{counts.partial}</span>
                </Badge>
              )}
              {counts.failed > 0 && (
                <Badge variant="outline" className="border-destructive/40 text-destructive">
                  <XCircle className="h-3 w-3 mr-1" />Failed <span className="ml-1 font-medium">{counts.failed}</span>
                </Badge>
              )}
              {(counts.publishing + counts.pending) > 0 && (
                <Badge variant="outline" className="border-blue-400/40 text-blue-300">
                  <Loader2 className={`h-3 w-3 mr-1 ${polling ? 'animate-spin' : ''}`} />
                  In flight <span className="ml-1 font-medium">{counts.publishing + counts.pending}</span>
                </Badge>
              )}
              <span className="ml-auto text-[10px] text-muted-foreground">
                {targets.length} target{targets.length === 1 ? '' : 's'} total
              </span>
              {(counts.failed + counts.partial) > 0 && (
                <Button size="sm" variant="ghost" className="h-7 text-[10px]"
                        onClick={() => setFilterFailed((v) => !v)}
                        data-testid="d91-filter-failed">
                  {filterFailed ? 'Show all' : 'Show failed only'}
                </Button>
              )}
            </div>

            {/* Targets list */}
            <div className="flex-1 overflow-y-auto border border-border/30 rounded-md mt-2"
                 data-testid="d91-target-list">
              {filteredTargets.length === 0 ? (
                <div className="p-6 text-[12px] text-muted-foreground text-center">
                  {filterFailed ? 'No failed targets — everything went through.' : 'No targets to show.'}
                </div>
              ) : (
                filteredTargets.map((t) => {
                  const stale = t.status === 'failed' || t.status === 'partial';
                  // First time we see a failed/partial row, auto-expand it
                  // ONCE by seeding the expanded map. After that, the
                  // user is free to collapse it.
                  const key = t.social_account_id;
                  const userToggled = key in expanded;
                  const isOpen = userToggled ? expanded[key] : stale;
                  return (
                    <TargetRow
                      key={key}
                      target={t}
                      expanded={isOpen}
                      onToggle={() => setExpanded((m) => ({ ...m, [key]: !isOpen }))}
                    />
                  );
                })
              )}
            </div>
          </>
        )}

        <DialogFooter className="mt-3">
          {hasRetryable && (
            <Button onClick={retry} disabled={retrying || polling} variant="outline"
                    data-testid="d91-retry-btn">
              {retrying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Retry failed ({counts.failed + counts.pending})
            </Button>
          )}
          <Button onClick={() => { stopPolling(); onClose && onClose(); }} data-testid="d91-close-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
